import { useAuthStore } from '@tundr/auth';
import { LifecyclePhase, useLifecycleHooksStore } from '@tundr/lifecycle-hooks';
import { useLocale } from '@tundr/i18n';
import { useToast } from '@tundr/toast';
import { useConfirmNavigationGuard } from '@tundr/routing';
import { useRouter } from 'vue-router';
import { appRoutesDictionary } from '@core/router/app-routes.dictionary';
import { useWizard } from '@tundr/wizard-stepper';
import { AUTH_FLOW_NAMESPACES } from '@modules/auth/config/auth-flows';
import { shutdown } from '@intercom/messenger-js-sdk';

export const useLogout = () => {
  const { t } = useLocale('common');
  const authStore = useAuthStore();
  const lifecycleHooks = useLifecycleHooksStore();
  const authFlows = AUTH_FLOW_NAMESPACES.map((namespace) =>
    useWizard(namespace),
  );
  const { confirmNavigationGuard } = useConfirmNavigationGuard();

  const router = useRouter();
  const { showSuccess } = useToast();

  const onLogout = async (config: { hideNotification?: boolean } = {}) => {
    const isExitConfirmed = await confirmNavigationGuard();

    if (!isExitConfirmed) {
      return;
    }

    try {
      await lifecycleHooks.run(LifecyclePhase.LOGOUT);
    } catch (err) {
      console.error(
        '[logout] lifecycle hook failed, forcing session teardown',
        err,
      );
    } finally {
      for (const flow of authFlows) {
        flow.destroyStepper();
      }
      authStore.logout();
      lifecycleHooks.reset(LifecyclePhase.SESSION_INIT);
      shutdown();
      if (!config.hideNotification) {
        showSuccess({
          message: t('profile_menu.actions.logout.success.title'),
          description: t('profile_menu.actions.logout.success.description'),
        });
      }
      router.replace({ name: appRoutesDictionary.auth.login.name });
    }
  };

  return {
    onLogout,
  };
};
